/**
 * MODULE: home/HomeExtras.jsx
 * PURPOSE: Homepage tail sections — Categories grid, "Why Trust DNH"
 *          methodology block, and the Newsletter signup card.
 *          Extracted from monolithic pages/Home.jsx (Phase 4 split).
 * PUBLIC API: <Categories />, <WhyTrust />, <Newsletter />
 * DEPENDS ON: utils/api.getCategories, POST /api/newsletter/subscribe
 * CALLED BY: pages/Home.jsx
 * LAST REVIEWED: 2026-07-17
 */
import React, { useState, useEffect } from "react";
import { ArrowRight, Bell } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { SectionHeading } from "../atoms";
import { getCategories } from "../../../utils/api";
import { usePlatformStats } from "./PlatformStatsContext";

export const Categories = () => {
  const [cats, setCats] = useState([]);

  useEffect(() => {
    getCategories().then((d) => {
      if (d?.categories?.length) setCats(d.categories);
    }).catch(() => {});
  }, []);

  return (
    <section
      className="py-14 sm:py-18 lg:py-24 border-t border-[var(--hairline)]"
      data-testid="categories-section"
      id="categories-section"
    >
      <div className="mx-auto max-w-[1280px] px-4 sm:px-6 lg:px-10">
        <SectionHeading
          label="Browse by domain"
          title="Intelligence categories"
          subtitle="Browse by domain, not by noise. Eight editorial verticals — each a stream of synthesized briefs."
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
          {cats.length === 0
            ? [1,2,3,4,5,6,7,8].map((i) => (
                <div key={i} className="rounded-[18px] border border-[var(--hairline)] bg-[var(--surface-2)] min-h-[176px] animate-pulse" />
              ))
            : cats.map((c, i) => (
                <Link
                  key={c.slug}
                  to={`/category/${c.slug}`}
                  className="dnh-cat-tile rounded-[18px] border border-[var(--hairline)] bg-[var(--paper)] p-6 flex flex-col gap-3 min-h-[176px] hover:border-[var(--hairline-strong)] transition-colors"
                  data-testid={`category-tile-${c.slug}`}
                >
                  <div className="flex items-baseline justify-between gap-3">
                    <h3 className="font-serif text-[22px] leading-tight text-[var(--ink)]">
                      {c.name}
                    </h3>
                    <span className="font-mono text-[10px] tracking-[0.1em] uppercase text-[var(--muted)]">
                      0{i + 1}
                    </span>
                  </div>
                  <p className="font-sans text-[13.5px] leading-[1.6] text-[var(--muted)]">
                    {c.description || ""}
                  </p>
                  <div className="mt-auto pt-3 border-t border-[var(--hairline)] flex items-center justify-between">
                    <span className="font-mono text-[10.5px] text-[var(--crimson)]">
                      Live · {c.signals_today ?? 0} briefs
                    </span>
                    <ArrowRight size={14} strokeWidth={2} className="text-[var(--ink-2)]" />
                  </div>
                </Link>
              ))}
        </div>
      </div>
    </section>
  );
};

/* ----------------------- Why Trust DNH ----------------------- */

export const WhyTrust = () => {
  const stats = usePlatformStats();
  return (
  <section
    className="py-14 sm:py-18 lg:py-24 border-t border-[var(--hairline)] bg-[var(--surface-2)]"
    data-testid="why-trust-section"
      id="why-trust-section"
  >
    <div className="mx-auto max-w-[1280px] px-4 sm:px-6 lg:px-10">
      <SectionHeading
        label="Methodology"
        title="Confidence is a system, not a claim"
        subtitle="DNH compresses fragmented global news into structured intelligence — with sources, contradictions and update velocity visible on every brief."
      />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[var(--hairline)] rounded-[18px] overflow-hidden border border-[var(--hairline)]">
        {[
          {
            n: "01",
            t: "Source diversity",
            b: "We triangulate across global wires, regional outlets and specialist publications — then show you how many independent sources support each brief.",
          },
          {
            n: "02",
            t: "Confidence layer",
            b: "Every story carries a confidence badge — High, Moderate or Developing — based on corroboration, contradiction and update velocity.",
          },
          {
            n: "03",
            t: "Editorial synthesis",
            b: "We compress complexity into a 10-minute brief: what happened, why it matters, and what to watch next — in journalist-grade prose.",
          },
        ].map((c) => (
          <div key={c.n} className="bg-[var(--paper)] p-6 sm:p-8" data-testid={`methodology-pillar-${c.n}`}>
            <div className="font-mono text-[11px] tracking-[0.18em] uppercase text-[var(--crimson)]">
              {c.n}
            </div>
            <h3 className="font-serif text-[22px] sm:text-[24px] leading-[1.15] text-[var(--ink)] mt-3">
              {c.t}
            </h3>
            <p className="font-sans text-[14.5px] leading-[1.65] text-[var(--ink-2)] mt-3">
              {c.b}
            </p>
          </div>
        ))}
      </div>

      <div className="mt-8 rounded-[14px] border border-[var(--hairline)] bg-[var(--paper)] px-6 py-5 grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-2">
        {[
          { v: stats?.stories_count?.toLocaleString() ?? "—", k: "Stories tracked" },
          { v: "English", k: "Language" },
          { v: stats?.countries_count ?? "—", k: "Countries indexed" },
          { v: "15 min", k: "Refresh cadence" },
        ].map((s) => (
          <div key={s.k} className="flex flex-col" data-testid={`methodology-stat-${s.k.toLowerCase().replaceAll(" ", "-")}`}>
            <span className="font-serif text-[24px] sm:text-[28px] leading-tight text-[var(--ink)]">
              {s.v}
            </span>
            <span className="font-mono text-[10.5px] tracking-[0.14em] uppercase text-[var(--muted)] mt-1">
              {s.k}
            </span>
          </div>
        ))}
      </div>
    </div>
  </section>
  );
};

/* ----------------------- Newsletter ----------------------- */

const BASE_URL_NL = process.env.REACT_APP_BACKEND_URL;

export const Newsletter = ({ compact = false }) => {
  const [email, setEmail] = React.useState("");
  const [status, setStatus] = React.useState("idle"); // idle | loading | success | error | duplicate
  const [error, setError] = React.useState("");

  const handleSubmit = async (e, resendConfirmation = false) => {
    e.preventDefault();
    if (status === "loading") return;
    if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setError("Please enter a valid email address.");
      setStatus("error");
      return;
    }
    setStatus("loading");
    setError("");
    try {
      const res = await fetch(`${BASE_URL_NL}/api/newsletter/subscribe`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim(), resend_confirmation: resendConfirmation }),
      });
      const data = await res.json();
      if (res.ok) {
        setStatus(data.confirmation_status === "accepted" ? "success" : "duplicate");
      } else {
        setError(typeof data.detail === "string" ? data.detail : "We couldn’t complete your subscription. Please try again.");
        setStatus("error");
      }
    } catch {
      setError("We couldn’t connect. Please try again.");
      setStatus("error");
    }
  };

  return (
  <section
    className={compact ? "py-4 max-w-xl" : "py-14 sm:py-18 lg:py-24 border-t border-[var(--hairline)]"}
    data-testid="newsletter-section"
      id="newsletter-section"
  >
    <div className={compact ? "" : "mx-auto max-w-[1100px] px-4 sm:px-6 lg:px-10"}>
      <div className={compact ? "relative" : "rounded-[18px] border border-[var(--hairline)] bg-[var(--paper)] p-8 sm:p-12 text-center relative overflow-hidden"}>
        <div className="absolute inset-0 dnh-paper-grain opacity-60 pointer-events-none" />
        <div className="relative">
          <div className="inline-flex items-center gap-2 font-mono text-[10.5px] tracking-[0.2em] uppercase text-[var(--crimson)]" data-testid="newsletter-label">
            <Bell size={12} strokeWidth={1.75} /> The Daily Brief
          </div>
          <h2 data-testid="newsletter-title" className={compact ? "font-serif text-lg text-[var(--ink)] mt-3" : "font-serif text-[32px] sm:text-[44px] lg:text-[56px] leading-[1.05] text-[var(--ink)] mt-4"}>
            One email. <span className="italic">The day&apos;s signal.</span> No noise.
          </h2>
          <p data-testid="newsletter-description" className="font-sans text-[15px] sm:text-base text-[var(--ink-2)] mt-4 max-w-xl mx-auto">
            A 6-minute read with the most consequential developments —
            ranked by confidence, written for decision-makers.
          </p>
          <p className="font-mono text-[10px] text-[var(--muted)] mt-3" data-testid="newsletter-cadence">Weekly edition · Sunday, 08:00 UTC</p>

          {status === "success" ? (
            <div className="mt-8 max-w-[560px] mx-auto flex flex-col items-center gap-3" role="status" data-testid="newsletter-success">
              <div className="inline-flex items-center gap-2 h-12 px-6 rounded-[12px] bg-[var(--ink)] text-[var(--paper)] font-medium text-[14px]">
                You&apos;re on the list.
              </div>
              <p className="text-sm text-[var(--muted)]" data-testid="newsletter-confirmation-status">Your confirmation email is on its way. Please check your inbox or spam folder.</p>
            </div>
          ) : status === "duplicate" ? (
            <div className="mt-8 max-w-[560px] mx-auto" data-testid="newsletter-duplicate">
              <p className="font-mono text-[11.5px] text-[var(--muted)]" role="status">You&apos;re already subscribed. Didn&apos;t receive the confirmation?</p>
            </div>
          ) : (
            <form
              noValidate
              className="mt-8 max-w-[560px] mx-auto flex flex-col sm:flex-row gap-3"
              onSubmit={handleSubmit}
              data-testid="newsletter-form"
            >
              <Input
                type="email"
                aria-label="Email address"
                autoComplete="email"
                required
                disabled={status === "loading"}
                value={email}
                onChange={(e) => { setEmail(e.target.value); setStatus("idle"); }}
                placeholder="you@company.com"
                className="h-12 sm:h-[52px] rounded-[12px] border border-[var(--hairline)] bg-[var(--paper)] px-4 text-[14.5px] placeholder:text-[var(--muted)] focus-visible:ring-2 focus-visible:ring-[var(--crimson)] focus-visible:ring-offset-0"
                data-testid="newsletter-email-input"
              />
              <Button
                type="submit"
                disabled={status === "loading"}
                className="h-12 sm:h-[52px] px-6 shrink-0 rounded-[12px] bg-[var(--crimson)] hover:bg-[var(--crimson-hover)] text-[var(--paper)] font-medium text-[14px] disabled:opacity-60"
                data-testid="newsletter-submit-button"
              >
                {status === "loading" ? "Subscribing…" : "Subscribe free"}
                {status !== "loading" && <ArrowRight size={16} strokeWidth={2} className="ml-2" />}
              </Button>
            </form>
          )}
          {status === "error" && (
            <p role="alert" className="font-mono text-[11px] text-[var(--crimson)] mt-2" data-testid="newsletter-error">{error}</p>
          )}
          {["success", "duplicate", "error"].includes(status) && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()) && <button type="button" onClick={e => handleSubmit(e, true)} className="mt-3 min-h-11 text-xs text-[var(--crimson)] underline underline-offset-4 hover:text-[var(--crimson-hover)] transition-colors" data-testid="newsletter-resend-confirmation">Resend confirmation</button>}
          <p data-testid="newsletter-assurance" className="font-mono text-[10.5px] text-[var(--muted)] mt-4">
            No spam. Unsubscribe in one click. We never sell your data.
          </p>
        </div>
      </div>
    </div>
  </section>
  );
};
